import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, CheckCircle2, Ban, Trash2, ScrollText, Printer, MessageCircle, FileDown, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HEAD_OFFICE } from "@/lib/company";
import { printMarketVehicles, printMarketMargin } from "@/lib/print";
import { inr, fmtDate, today, parseTermDate, parseTermMonth } from "@/lib/format";
import { BRANCHES } from "@/lib/branches";
import { toast } from "sonner";

const EMPTY = { date: "", branch: "", from_place: "", destination: "", customer_code: "", customer_name: "", vendor_name: "", vehicle_type: "", vehicle_no: "", weight: "", vehicle_hire: 0, freight: 0, remarks: "" };
const COLS = ["Sel", "Sl No", "Branch", "Engaged Date", "From", "Destination", "Customer Name", "Customer Code", "Vendor/Broker Name", "Vehicle No.", "Vehicle Type", "Weight", "Vehicle Hire", "Freight", "Margin", "Status", "Actions"];

const statusTone = (s) =>
  s === "Approved"
    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
    : s === "Rejected"
    ? "border-red-300 bg-red-50 text-red-600"
    : s === "Challan Issued"
    ? "border-sky-300 bg-sky-50 text-sky-700"
    : "border-amber-300 bg-amber-50 text-amber-700";

export default function MarketVehicle() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [marginPct, setMarginPct] = useState(() => Number(localStorage.getItem("ntc_mv_margin_pct") || 10));
  const setMarginThreshold = (v) => {
    const n = Math.max(0, Number(v) || 0);
    setMarginPct(n);
    localStorage.setItem("ntc_mv_margin_pct", String(n));
  };
  const marginOf = (r) => (Number(r.freight) || 0) - (Number(r.vehicle_hire) || 0);
  const lowMargin = (r) => (Number(r.freight) || 0) > 0 && (marginOf(r) / Number(r.freight)) * 100 < marginPct;
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sel, setSel] = useState(() => new Set());
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = useCallback(() => {
    api.get("/market-vehicles").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.from_place.trim() || !form.destination.trim()) { toast.error("Enter From and Destination"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/market-vehicles/${editing.id}`, { ...form, vehicle_hire: Number(form.vehicle_hire) || 0, freight: Number(form.freight) || 0 });
        toast.success("Request updated");
      } else {
        const res = await api.post("/market-vehicles", { ...form, vehicle_hire: Number(form.vehicle_hire) || 0, freight: Number(form.freight) || 0 });
        toast.success("Request sent to Admin for approval", {
          duration: 10000,
          action: { label: "WhatsApp Admin", onClick: () => whatsappPing(res.data) },
        });
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (r, status, why = "") => {
    setBusy(r.id);
    try {
      await api.put(`/market-vehicles/${r.id}/status`, { status, reason: why });
      toast.success(`Request ${status.toLowerCase()}`);
      setRejecting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/market-vehicles/${r.id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const txt = (k, label, placeholder) => (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Input value={form[k] ?? ""} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} data-testid={`mv-field-${k}`} />
    </div>
  );

  const hasRange = !!(dateFrom || dateTo);
  const qTrim = q.trim().toLowerCase();
  const qDate = qTrim ? parseTermDate(qTrim) : null;
  const qMonth = qTrim && !qDate ? parseTermMonth(qTrim) : null;
  const shown = (rows || [])
    .filter((r) => {
      if (qDate) return (r.date || "").slice(0, 10) === qDate;
      if (qMonth) return (r.date || "").slice(0, 7) === qMonth;
      if (hasRange) {
        const d = r.date || "";
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      }
      return !month || (r.date || "").startsWith(month);
    })
    .filter((r) => {
      if (!qTrim || qDate || qMonth) return true;
      const hay = [r.customer_name, r.customer_code, r.vendor_name, r.vehicle_no, r.vehicle_type, r.from_place, r.destination, r.branch, r.status];
      return hay.some((x) => String(x || "").toLowerCase().includes(qTrim));
    });
  const exportRows = sel.size ? shown.filter((r) => sel.has(r.id)) : shown;
  const allSel = shown.length > 0 && shown.every((r) => sel.has(r.id));
  const toggleAllSel = () => setSel((p) => { const n = new Set(p); const all = shown.every((r) => n.has(r.id)); shown.forEach((r) => (all ? n.delete(r.id) : n.add(r.id))); return n; });
  const monthLabel = hasRange
    ? `${dateFrom ? fmtDate(dateFrom) : "…"} to ${dateTo ? fmtDate(dateTo) : "…"}`
    : month
    ? new Date(`${month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : "All";

  const whatsappPing = (r) => {
    const msg = encodeURIComponent(
      [
        `*MARKET VEHICLE REQUEST — ${r.branch || ""}*`,
        `Date: ${r.date ? fmtDate(r.date) : ""}`,
        `From: ${r.from_place || ""} → To: ${r.destination || ""}`,
        `Customer: ${r.customer_name || ""}`,
        `Vendor/Broker: ${r.vendor_name || ""}`,
        `Vehicle Type: ${r.vehicle_type || ""} | Vehicle No: ${r.vehicle_no || "TBD"} | Weight: ${r.weight || ""}`,
        `Vehicle Hire: Rs.${Number(r.vehicle_hire) || 0} | Freight: Rs.${Number(r.freight) || 0} | Margin: Rs.${(Number(r.freight) || 0) - (Number(r.vehicle_hire) || 0)}`,
        `Status: ${r.status || "Pending"}${(r.status || "Pending") === "Pending" ? " — please approve" : ""}`,
      ].join("\n")
    );
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${msg}`, "_blank");
  };

  const marginExcel = () => {
    const src = shown.filter((r) => r.status === "Approved" || r.status === "Challan Issued");
    const byBranch = {};
    src.forEach((r) => {
      const b = r.branch || "—";
      const a = (byBranch[b] = byBranch[b] || { trips: 0, freight: 0, hire: 0 });
      a.trips += 1;
      a.freight += Number(r.freight) || 0;
      a.hire += Number(r.vehicle_hire) || 0;
    });
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      [`Market Vehicle Margin Report — ${monthLabel} (Approved / Challan Issued trips only)`],
      ["Sl No", "Branch", "Trips", "Freight", "Vehicle Hire", "Margin", "Margin %"],
    ];
    const t = { trips: 0, freight: 0, hire: 0 };
    Object.entries(byBranch).sort((a, b) => a[0].localeCompare(b[0])).forEach(([b, a], i) => {
      t.trips += a.trips; t.freight += a.freight; t.hire += a.hire;
      const m = a.freight - a.hire;
      lines.push([i + 1, b, a.trips, a.freight, a.hire, m, a.freight > 0 ? ((m / a.freight) * 100).toFixed(1) + "%" : ""]);
    });
    const tm = t.freight - t.hire;
    lines.push(["", "TOTAL", t.trips, t.freight, t.hire, tm, t.freight > 0 ? ((tm / t.freight) * 100).toFixed(1) + "%" : ""]);
    const blob = new Blob(["\ufeff" + lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `market-margin-${month || "all"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const whatsappLowMargin = () => {
    const low = shown
      .filter((r) => lowMargin(r))
      .sort((a, b) => (marginOf(a) / (Number(a.freight) || 1)) - (marginOf(b) / (Number(b.freight) || 1)));
    if (!low.length) { toast.info(`No low-margin (below ${marginPct}%) market vehicles for ${monthLabel}`); return; }
    const cap = 20;
    const lines = low.slice(0, cap).map((r, i) => {
      const m = marginOf(r);
      const pct = (Number(r.freight) || 0) > 0 ? ((m / Number(r.freight)) * 100).toFixed(1) : "0";
      return `${i + 1}. ${r.date ? fmtDate(r.date) : ""} | ${r.branch || "—"} | ${r.from_place || ""}→${r.destination || ""} | ${r.vehicle_no || "TBD"} | Hire Rs.${Number(r.vehicle_hire) || 0} | Frt Rs.${Number(r.freight) || 0} | Margin Rs.${m} (${pct}%)`;
    });
    const totF = low.reduce((s, r) => s + (Number(r.freight) || 0), 0);
    const totM = low.reduce((s, r) => s + marginOf(r), 0);
    const msg = encodeURIComponent(
      [
        `*LOW MARGIN MARKET VEHICLES — ${monthLabel}*`,
        `Threshold: below ${marginPct}% | Trips: ${low.length}`,
        `Total Freight: Rs.${totF} | Total Margin: Rs.${totM}`,
        "",
        ...lines,
        low.length > cap ? `…and ${low.length - cap} more (see Margin Report)` : "",
        "",
        "— NTC ERP",
      ].filter(Boolean).join("\n")
    );
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${msg}`, "_blank");
    toast.success(`WhatsApp opened with ${low.length} low-margin trip${low.length > 1 ? "s" : ""} for head office`);
  };

  const excel = () => {
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      [`Market Vehicle Engaged — ${monthLabel}`],
      ["Sl No", "Branch", "Engaged Date", "From", "Destination", "Customer Name", "Customer Code", "Vendor/Broker Name", "Vehicle No.", "Vehicle Type", "Weight", "Vehicle Hire", "Freight", "Margin", "Status", "Reject Reason"],
      ...exportRows.map((r, i) => [i + 1, r.branch || "", r.date ? fmtDate(r.date) : "", r.from_place || "", r.destination || "", r.customer_name || "", r.customer_code || "", r.vendor_name || "", r.vehicle_no || "", r.vehicle_type || "", r.weight || "", r.vehicle_hire || 0, r.freight || 0, (Number(r.freight) || 0) - (Number(r.vehicle_hire) || 0), r.status || "Pending", r.status === "Rejected" ? r.decided_reason || "" : ""]),
    ];
    const blob = new Blob(["\ufeff" + lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `market-vehicles-${month || "all"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div data-testid="market-vehicle-page">
      <PageHeader
        title="Market Vehicle Engaged"
        subtitle="Branch requests for hiring market vehicles — Admin approval required."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
              <span className="text-xs font-medium text-amber-800">Low margin below</span>
              <Input type="number" min="0" value={marginPct} onChange={(e) => setMarginThreshold(e.target.value)} className="h-6 w-14 border-amber-300 bg-white px-1 text-center text-xs" data-testid="mv-margin-threshold" />
              <span className="text-xs font-medium text-amber-800">%</span>
              <button
                type="button"
                title="WhatsApp this month's low-margin vehicles to head office"
                className="ml-1 inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-emerald-700"
                onClick={whatsappLowMargin}
                data-testid="mv-low-margin-whatsapp-btn"
              >
                <MessageCircle className="h-3 w-3" /> WhatsApp
              </button>
            </div>
            <div className="relative">
              <Input
                className="h-9 w-[200px] pl-3 pr-7"
                placeholder="Search customer / code / vehicle / 08-2026"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="mv-search"
              />
              {q && (
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="mv-search-clear">✕</button>
              )}
            </div>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-[160px]" disabled={hasRange} data-testid="mv-month-filter" />
            <div className="flex items-center gap-1 rounded-md border border-input bg-white px-2 py-1">
              <span className="text-xs text-slate-500">From</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 w-[135px] border-0 px-1 text-xs shadow-none" data-testid="mv-date-from" />
              <span className="text-xs text-slate-500">To</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 w-[135px] border-0 px-1 text-xs shadow-none" data-testid="mv-date-to" />
              {hasRange && (
                <button type="button" className="text-slate-400 hover:text-slate-600" title="Clear date range" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="mv-date-clear">✕</button>
              )}
            </div>
            <Button variant="outline" onClick={excel} data-testid="mv-excel-btn">
              <FileDown className="mr-2 h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" onClick={() => printMarketMargin(shown.filter((r) => r.status === "Approved" || r.status === "Challan Issued"), monthLabel)} data-testid="mv-margin-report-btn">
              <Printer className="mr-2 h-4 w-4" /> Margin Report
            </Button>
            <Button variant="outline" onClick={marginExcel} data-testid="mv-margin-excel-btn">
              <FileDown className="mr-2 h-4 w-4" /> Margin Excel
            </Button>
            <Button variant="outline" onClick={() => printMarketVehicles(exportRows, sel.size ? `${monthLabel} (${sel.size} selected)` : monthLabel)} data-testid="mv-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button onClick={() => { setEditing(null); setForm({ ...EMPTY, date: today(), branch: isAdmin ? "" : user?.office || "" }); setOpen(true); }} data-testid="add-market-vehicle-btn">
              <Plus className="mr-2 h-4 w-4" /> Request Market Vehicle
            </Button>
          </div>
        }
      />
      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={toggleAllSel} data-testid="mv-select-all" /></TableHead>
              {COLS.slice(1).map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-[11px] uppercase tracking-[0.1em] text-slate-500">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>{COLS.map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
              ))}
            {rows && shown.length === 0 && (
              <TableRow><TableCell colSpan={COLS.length} className="py-12 text-center text-muted-foreground">No market vehicle requests for {monthLabel}.</TableCell></TableRow>
            )}
            {shown.map((r, i) => (
              <TableRow key={r.id} data-testid="market-vehicle-row" className={lowMargin(r) && (r.status || "Pending") === "Pending" ? "bg-amber-50/70 hover:bg-amber-50" : undefined}>
                <TableCell><Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid="mv-select-row" /></TableCell>
                <TableCell className="text-sm">{i + 1}</TableCell>
                <TableCell className="text-sm font-medium">{r.branch || "—"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap" data-testid="mv-engaged-date">{r.date ? fmtDate(r.date) : "—"}</TableCell>
                <TableCell className="text-sm">{r.from_place || "—"}</TableCell>
                <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm">{r.customer_code || "—"}</TableCell>
                <TableCell className="text-sm">{r.vendor_name || "—"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap" data-testid="mv-vehicle-no-cell">{r.vehicle_no || "—"}</TableCell>
                <TableCell className="text-sm">{r.vehicle_type || "—"}</TableCell>
                <TableCell className="text-sm">{r.weight || "—"}</TableCell>
                <TableCell className="text-sm tabular-nums">{inr(r.vehicle_hire)}</TableCell>
                <TableCell className="text-sm tabular-nums">{inr(r.freight)}</TableCell>
                <TableCell className={`text-sm font-semibold tabular-nums ${marginOf(r) >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="mv-margin-cell">
                  {inr(marginOf(r))}
                  {lowMargin(r) && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid="mv-low-margin-badge">
                      LOW &lt;{marginPct}%
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={statusTone(r.status)} data-testid="mv-status-badge">{r.status || "Pending"}</Badge>
                  {r.status === "Challan Issued" && r.challan_no && (
                    <button
                      type="button"
                      className="mt-1 block text-xs font-medium text-sky-700 underline underline-offset-2 hover:text-sky-900"
                      onClick={() => navigate(r.challan_id ? `/hire-challans?open=${r.challan_id}` : "/hire-challans")}
                      data-testid="mv-challan-no"
                    >
                      Challan {r.challan_no}
                    </button>
                  )}
                  {r.status === "Rejected" && r.decided_reason && (
                    <div className="mt-1 max-w-[180px] text-xs text-red-600" data-testid="mv-reject-reason">{r.decided_reason}</div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {isAdmin && r.status === "Pending" && (
                      <>
                        <Button size="sm" className="h-7 bg-emerald-600 px-2 text-xs hover:bg-emerald-700" disabled={busy === r.id} onClick={() => decide(r, "Approved")} data-testid="mv-approve-btn">
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 border-red-300 px-2 text-xs text-red-600 hover:bg-red-50" disabled={busy === r.id} onClick={() => { setReason(""); setRejecting(r); }} data-testid="mv-reject-btn">
                          <Ban className="mr-1 h-3.5 w-3.5" /> Reject
                        </Button>
                      </>
                    )}
                    {r.status === "Approved" && (
                      <Button size="sm" variant="outline" className="h-7 border-sky-300 px-2 text-xs text-sky-700 hover:bg-sky-50" onClick={() => navigate(`/hire-challans?from_market=${r.id}`)} data-testid="mv-create-challan-btn">
                        <ScrollText className="mr-1 h-3.5 w-3.5" /> Create Challan
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-primary" title="Edit (change Hire & Freight)" onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); }} data-testid="mv-edit-btn">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700" title="Send to Admin on WhatsApp" onClick={() => whatsappPing(r)} data-testid="mv-whatsapp-btn">
                      <MessageCircle className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => remove(r)} data-testid="mv-delete-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="market-vehicle-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Market Vehicle Request" : "Request Market Vehicle"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="mv-field-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Branch</Label>
              {isAdmin ? (
                <Select value={form.branch} onValueChange={(v) => set("branch", v)}>
                  <SelectTrigger data-testid="mv-field-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={form.branch} disabled data-testid="mv-field-branch" />
              )}
            </div>
            {txt("from_place", "From", "e.g. Chennai")}
            {txt("destination", "Destination", "e.g. Hyderabad")}
            <div className="grid gap-1.5">
              <Label>Customer Code</Label>
              <Input
                value={form.customer_code ?? ""}
                placeholder="e.g. CUST-1001"
                data-testid="mv-field-customer_code"
                onChange={(e) => {
                  const v = e.target.value;
                  const key = v.trim().toLowerCase();
                  const c = key ? customers.find((x) => (x.customer_code || "").toLowerCase() === key) : null;
                  setForm((f) => ({ ...f, customer_code: v, ...(c ? { customer_name: c.name } : {}) }));
                }}
              />
            </div>
            {txt("customer_name", "Customer Name", "Customer")}
            <div className="grid gap-1.5">
              <Label>Vendor / Broker (who is quoting)</Label>
              <Input
                value={form.vendor_name ?? ""}
                placeholder="Market vendor / broker name"
                list="mv-vendor-list"
                onChange={(e) => set("vendor_name", e.target.value)}
                data-testid="mv-field-vendor_name"
              />
              <datalist id="mv-vendor-list">
                {vendors.map((v) => <option key={v.id} value={v.name} />)}
              </datalist>
            </div>
            {txt("vehicle_type", "Vehicle Type", "e.g. 20 ft Container")}
            {txt("vehicle_no", "Vehicle No. (once fixed)", "e.g. TN 01 AB 1234")}
            {txt("weight", "Weight", "e.g. 8500 Kgs")}
            <div className="grid gap-1.5">
              <Label>Vehicle Hire (₹)</Label>
              <Input type="number" value={form.vehicle_hire} onChange={(e) => set("vehicle_hire", e.target.value)} data-testid="mv-field-vehicle_hire" />
            </div>
            <div className="grid gap-1.5">
              <Label>Freight (₹)</Label>
              <Input type="number" value={form.freight} onChange={(e) => set("freight", e.target.value)} data-testid="mv-field-freight" />
            </div>
            {txt("remarks", "Remarks", "Optional")}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="mv-save-btn">{saving ? "Saving…" : editing ? "Save Changes" : "Send Request"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="mv-reject-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject Request</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {rejecting ? `${rejecting.branch || ""}: ${rejecting.from_place || ""} → ${rejecting.destination || ""}` : ""}
          </p>
          <div className="grid gap-1.5">
            <Label>Reason (shown to the branch)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Hire amount too high — negotiate below ₹40,000" data-testid="mv-reject-reason-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button variant="destructive" disabled={busy === rejecting?.id} onClick={() => decide(rejecting, "Rejected", reason)} data-testid="mv-reject-confirm-btn">
              <Ban className="mr-2 h-4 w-4" /> Reject Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
